import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WarningCircle, Flag, ListChecks, CheckCircle, Clock, SquaresFour } from "@phosphor-icons/react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { format } from "date-fns";

const StatCard = ({ icon: Icon, label, value, testId, to }) => (
  <Link to={to} data-testid={testId} className="card-flat p-4 flex items-center gap-3 hover:shadow-md hover:border-primary/40 transition-all cursor-pointer">
    <div className="w-9 h-9 flex items-center justify-center bg-muted" style={{ borderRadius: "2px" }}><Icon size={18} className="text-primary" /></div>
    <div>
      <div className="text-2xl font-display font-extrabold leading-none">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  </Link>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-10 text-center font-mono text-muted-foreground" data-testid="dashboard-loading">&gt; loading dashboard...</div>;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-2">
        <span className="label-caps">Workspace Overview</span>
        <h1 className="text-2xl font-display font-extrabold mt-1">Dashboard</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={ListChecks} label="Active Projects" value={data.active_projects_count} testId="stat-active-projects" to="/projects" />
        <StatCard icon={CheckCircle} label="Completed Tasks" value={data.stats.completed_tasks} testId="stat-completed-tasks" to="/tasks?status=Completed" />
        <StatCard icon={Clock} label="In Progress Tasks" value={data.stats.in_progress_tasks} testId="stat-in-progress-tasks" to="/tasks?status=In Progress" />
        <StatCard icon={WarningCircle} label="Overdue Tasks" value={data.stats.overdue_tasks} testId="stat-overdue-tasks" to="/tasks?status=Overdue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 card-flat p-5">
          <span className="label-caps"><WarningCircle size={12} />At-Risk Projects</span>
          <div className="mt-3 space-y-2">
            {data.at_risk_projects.length === 0 && <div className="text-sm font-mono text-muted-foreground" data-testid="no-at-risk-projects">&gt; no at-risk projects — all clear</div>}
            {data.at_risk_projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`at-risk-project-${p.id}`} className="flex items-center justify-between p-3 border border-border hover:bg-muted transition-colors" style={{ borderRadius: "2px" }}>
                <span className="font-medium text-sm">{p.name}</span>
                <span className="text-xs text-destructive font-semibold">{p.overdue_count} overdue · {p.completion_pct}% complete</span>
              </Link>
            ))}
          </div>
        </div>
        <div className="card-flat p-5">
          <span className="label-caps"><Flag size={12} />Upcoming Milestones</span>
          <div className="mt-3 space-y-3">
            {data.upcoming_milestones.length === 0 && <div className="text-sm font-mono text-muted-foreground">&gt; none in next 14 days</div>}
            {data.upcoming_milestones.map((m) => (
              <div key={m.id} data-testid={`upcoming-milestone-${m.id}`} className="text-sm">
                <div className="font-semibold">{m.name}</div>
                <div className="text-xs text-muted-foreground">{m.project_name} · {format(new Date(m.target_date), "d MMM yyyy")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div data-testid="dashboard-projects-section">
        <div className="flex items-center justify-between mb-3">
          <span className="label-caps"><SquaresFour size={12} />Projects</span>
          <Link to="/projects" data-testid="dashboard-view-all-projects" className="text-xs font-semibold uppercase text-primary hover:underline">View All</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-projects-grid">
          {projects.length === 0 && <div className="text-sm font-mono text-muted-foreground col-span-full">&gt; no active projects yet</div>}
          {projects.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} data-testid={`dashboard-project-tile-${p.id}`} className="card-flat p-4 hover:shadow-md hover:border-primary/40 transition-all block">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-display font-bold text-sm">{p.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.product_line}</div>
                </div>
                <StatusBadge status={p.status} />
                {p.is_draft && <span className="text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-700 ml-1" style={{ borderRadius: "2px" }}>Draft</span>}
              </div>
              <div className="w-full h-1.5 bg-muted mt-3" style={{ borderRadius: "2px" }}>
                <div className="h-1.5 bg-accent" style={{ width: `${p.completion_pct}%`, borderRadius: "2px" }} />
              </div>
              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                <span>{p.completion_pct}% complete</span>
                {p.overdue_count > 0 && <span className="text-destructive font-semibold">{p.overdue_count} overdue</span>}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-xs">
                <span>Owner: <span className="font-medium">{p.owner_name || "—"}</span></span>
                <span className="font-mono">{p.target_production_date ? format(new Date(p.target_production_date), "d MMM yyyy") : "—"}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
